import React, { useMemo } from 'react';
import { matchPath, useLocation, useNavigate, Link } from 'react-router';
import {
  Button,
  CondensedHeader,
  CondensedLarge,
  Form,
  FormGroup,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Toggle,
  useApi,
} from '@axiom/ui';
import type { UserCustomTalentAlert } from '@axiom/validation';
import { z } from 'zod';
import { UserCustomTalentAlertsConst } from '@axiom/const';

import { UserAlertsApi } from '../../api/user-alerts-api';
import { UserSearchesApi } from '../../api/user-searches-api';

import { ManageEmailAlertsModalSchema } from './manage-email-alerts-modal-schema';

export interface ManageEmailAlertsModalProps {
  onClose: () => void;
  onNavigateToCandidate: () => void;
  onNavigateToSavedSearches: () => void;
}

type GroupedTalentAlert = {
  candidateId: string;
  candidateName: string;
  availability?: UserCustomTalentAlert;
  rollOff?: UserCustomTalentAlert;
};

const groupTalentAlertsByCandidate = (
  alerts: UserCustomTalentAlert[]
): GroupedTalentAlert[] => {
  const grouped = new Map<string, GroupedTalentAlert>();

  for (const alert of alerts) {
    const group = grouped.get(alert.candidateId) ?? {
      candidateId: alert.candidateId,
      candidateName: alert.candidateName,
    };

    if (
      alert.alertType === UserCustomTalentAlertsConst.AlertType.Availability
    ) {
      group.availability = alert;
    } else {
      group.rollOff = alert;
    }

    grouped.set(alert.candidateId, group);
  }

  return [...grouped.values()];
};

export const ManageEmailAlertsModal = ({
  onClose,
  onNavigateToCandidate,
  onNavigateToSavedSearches,
}: ManageEmailAlertsModalProps) => {
  const [{ data: alerts }] = useApi(UserAlertsApi.readUserAlerts());
  const { fixedAlerts, customAlerts } = alerts;
  const { savedSearchAlerts, talentAlerts } = customAlerts;

  const location = useLocation();
  const navigate = useNavigate();

  const groupedAlerts = useMemo(
    () => groupTalentAlertsByCandidate(talentAlerts),
    [talentAlerts]
  );

  const { initialValues, schema } = useMemo(() => {
    const talentAlertValues: Record<string, boolean> = {};
    const talentAlertShape: Record<string, z.ZodTypeAny> = {};
    for (const alert of talentAlerts) {
      const key = `talentAlert_${alert.id}`;
      talentAlertValues[key] = alert.alertActive;
      talentAlertShape[key] = z.boolean();
    }
    return {
      initialValues: {
        ...fixedAlerts,
        savedSearchAlerts,
        ...talentAlertValues,
      },
      schema: ManageEmailAlertsModalSchema.extend(talentAlertShape),
    };
  }, [fixedAlerts, talentAlerts, savedSearchAlerts]);

  return (
    <Form
      initialValues={initialValues}
      name="EMAIL_ALERTS_FORM"
      schema={schema}
      onSubmit={async (changedData, _actions, values) => {
        const { savedSearchAlerts: _s, ...updatedFixedAlerts } =
          ManageEmailAlertsModalSchema.parse(values);
        const { savedSearchAlerts: updatedSavedSearchAlerts } =
          ManageEmailAlertsModalSchema.partial().parse(changedData);

        const talentAlertUpdates = talentAlerts
          .filter(
            alert => alert.alertActive !== values[`talentAlert_${alert.id}`]
          )
          .map(async alert =>
            UserAlertsApi.updateCustomTalentAlert(alert.id, {
              candidateId: alert.candidateId,
              alertType: alert.alertType,
              alertActive: values[`talentAlert_${alert.id}`] as boolean,
              minAvailability: alert.minAvailability,
            })
          );

        await Promise.all([
          UserAlertsApi.updateUserFixedAlerts(updatedFixedAlerts),
          ...talentAlertUpdates,
        ]);

        if (updatedSavedSearchAlerts && updatedSavedSearchAlerts.length > 0) {
          await UserSearchesApi.updateUserSearches(updatedSavedSearchAlerts);
          await UserSearchesApi.refreshAllUserSearchTypes();
        }

        await UserAlertsApi.refreshUserAlerts();
        onClose();
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="ManageEmailAlertsModal">
          <ModalHeader
            name="ManageEmailAlertsModalHeader"
            onClose={() => {
              onClose();
            }}
          >
            Manage email alerts
          </ModalHeader>
          <ModalSection>
            <ModalSection name="CLIENT_ALERTS" disableScrolling>
              <CondensedLarge>
                Select Yes for each alert you'd like to receive by email.
              </CondensedLarge>
              <Gutter top="16px" bottom="16px">
                <CondensedHeader>Client alerts</CondensedHeader>
                <Gutter top="16px" bottom="8px">
                  <Layout>
                    <LayoutItem fluid>
                      <CondensedLarge>
                        Client creates an Axiom user account
                      </CondensedLarge>
                    </LayoutItem>
                    <Toggle name="clientCreatedAccountAlertActive" />
                  </Layout>
                </Gutter>
                <Layout>
                  <LayoutItem fluid>
                    <CondensedLarge>
                      Client signs in to their AFC account
                    </CondensedLarge>
                  </LayoutItem>
                  <Toggle name="clientSignedInAlertActive" />
                </Layout>
                <Gutter bottom="8px" />
                <Layout>
                  <LayoutItem fluid>
                    <CondensedLarge>
                      Client starts an engagement in AFC
                    </CondensedLarge>
                  </LayoutItem>
                  <Toggle name="clientCreatedOppAlertActive" />
                </Layout>
                <Gutter bottom="8px" />
                <Layout>
                  <LayoutItem fluid>
                    <CondensedLarge>
                      Client requests an interview in AFC
                    </CondensedLarge>
                  </LayoutItem>
                  <Toggle name="clientReqInterviewAlertActive" />
                </Layout>
                <Gutter bottom="8px" />
                <Layout>
                  <LayoutItem fluid>
                    <CondensedLarge>
                      Client searches for talent in AFC
                    </CondensedLarge>
                  </LayoutItem>
                  <Toggle name="clientSearchedInAfcAlertActive" />
                </Layout>
              </Gutter>
            </ModalSection>
            <ModalSection name="TALENT_ALERTS" divider disableScrolling>
              <Gutter top="16px" bottom="16px">
                <CondensedHeader>Talent alerts</CondensedHeader>
                <Gutter top="16px" bottom="8px">
                  <Layout>
                    <LayoutItem fluid>
                      <CondensedLarge>
                        Talent with experience at one of your accounts is on the
                        Beach
                      </CondensedLarge>
                    </LayoutItem>
                    <Toggle name="beachWithExperienceAlertActive" />
                  </Layout>
                </Gutter>
                <Layout>
                  <LayoutItem fluid>
                    <CondensedLarge>
                      New Waitlist talent has experience at one of your accounts
                    </CondensedLarge>
                  </LayoutItem>
                  <Toggle name="waitlistWithExperienceAlertActive" />
                </Layout>
                <Gutter bottom="8px" />
                <Layout>
                  <LayoutItem fluid>
                    <CondensedLarge>
                      Talent submitted on your opp is selected for a different
                      opp
                    </CondensedLarge>
                  </LayoutItem>
                  <Toggle name="talentSelectedOnOppAlertActive" />
                </Layout>
              </Gutter>
            </ModalSection>
            <ModalSection name="SAVED_SEARCH_ALERTS" divider disableScrolling>
              <Gutter top="16px" bottom="16px">
                <Layout>
                  <LayoutItem>
                    <CondensedHeader>Saved search alerts</CondensedHeader>
                  </LayoutItem>
                  <LayoutItem leftGutter="4px">
                    <a
                      data-test="SAVED_SEARCHES_LINK"
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        if (
                          matchPath(
                            { path: '/talent/:id', end: true },
                            location.pathname
                          )
                        ) {
                          void navigate('?showSavedSearches=true');
                        } else {
                          void navigate('/talent?showSavedSearches=true');
                        }
                        onNavigateToSavedSearches();
                      }}
                    >
                      View Saved Searches
                    </a>
                  </LayoutItem>
                </Layout>
                <Gutter top="16px">
                  {savedSearchAlerts.length > 0 ? (
                    savedSearchAlerts.map(({ id, name }, index) => {
                      return (
                        <FormGroup
                          name={`savedSearchAlerts.${index}`}
                          key={`saved-search-alert-group-${id}`}
                        >
                          <Layout>
                            <LayoutItem fluid>
                              <CondensedLarge
                                name={`saved-search-alert-${id}-name`}
                              >
                                {name}
                              </CondensedLarge>
                            </LayoutItem>
                            <Toggle
                              name={`savedSearchAlerts.${index}.alertActive`}
                            />
                          </Layout>
                          <Gutter bottom="8px" />
                        </FormGroup>
                      );
                    })
                  ) : (
                    <CondensedLarge name="SAVED_SEARCH_ALERTS_EMPTY">
                      You have not saved any searches
                    </CondensedLarge>
                  )}
                </Gutter>
              </Gutter>
            </ModalSection>
            <ModalSection name="CUSTOM_TALENT_ALERTS" divider disableScrolling>
              <Gutter top="16px" bottom="16px">
                <CondensedHeader>
                  Custom talent availability alerts
                </CondensedHeader>
                <Gutter top="16px">
                  {groupedAlerts.length === 0 ? (
                    <CondensedLarge>
                      You have not set up any alerts for talent availability
                    </CondensedLarge>
                  ) : (
                    groupedAlerts.map(group => (
                      <div
                        key={group.candidateId}
                        data-test={`TALENT_ALERT_GROUP_${group.candidateId}`}
                      >
                        {group.availability ? (
                          <Gutter bottom="8px">
                            <Layout>
                              <LayoutItem fluid>
                                <CondensedLarge>
                                  <Link
                                    data-test={`TALENT_ALERT_AVAILABILITY_LINK_${group.candidateId}`}
                                    to={`/talent-detail/${group.candidateId}`}
                                    onClick={() => {
                                      onNavigateToCandidate();
                                    }}
                                  >
                                    {group.candidateName}
                                  </Link>{' '}
                                  has at least{' '}
                                  {group.availability.minAvailability} hours of
                                  weekly availability
                                </CondensedLarge>
                              </LayoutItem>
                              <Toggle
                                name={`talentAlert_${group.availability.id}`}
                              />
                            </Layout>
                          </Gutter>
                        ) : null}
                        {group.rollOff ? (
                          <Gutter bottom="8px">
                            <Layout>
                              <LayoutItem fluid>
                                <CondensedLarge>
                                  <Link
                                    data-test={`TALENT_ALERT_ROLLOFF_LINK_${group.candidateId}`}
                                    to={`/talent-detail/${group.candidateId}`}
                                    onClick={() => {
                                      onNavigateToCandidate();
                                    }}
                                  >
                                    {group.candidateName}
                                  </Link>
                                  's engagement end date is within 2 weeks
                                </CondensedLarge>
                              </LayoutItem>
                              <Toggle
                                name={`talentAlert_${group.rollOff.id}`}
                              />
                            </Layout>
                          </Gutter>
                        ) : null}
                      </div>
                    ))
                  )}
                </Gutter>
              </Gutter>
            </ModalSection>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_BUTTON"
              onClick={() => {
                onClose();
              }}
              variation="outline"
            >
              Cancel
            </Button>
            <Button name="SAVE_BUTTON" onClick={fireSubmit}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
